// pms-core / domain — the canonical domain shapes for the PMS reservation
// domain. camelCase, English (repo R4); the snake_case DB rows and their
// mappers live in the Supabase adapter, never here. This package has ZERO
// runtime/infra dependencies — it is the pure core every port and adapter
// agrees on (hexagonal "domain" ring). See docs/PMS-P2-ARCHITECTURE-BRIEF.md.

import type {
  PmsBoard,
  PmsGuarantee,
  PmsReservationStatus,
  PmsSource,
} from './pms.js';
import type { ChildMealBand } from './rateParameters.js';
import type { SoldTerms } from './cancellationPolicy.js';

// ── Named primitives ─────────────────────────────────────────────────────────
// Plain aliases (not branded): they carry intent for readers without adding
// cast-noise at the DB/React boundaries, where everything is already plain
// string/number. A branded variant was considered and dropped — the friction
// against the existing plain-typed code + tests wasn't worth the marginal safety
// at 42-room scale (PMS-P2-ARCHITECTURE-BRIEF §4).

/** UUID string — a reservation. */
export type ReservationId = string;
/** UUID string — ONE ROOM of a booking, « un logement » (multi-room brief §3).
 *  A two-room booking is two of these; each carries its own dates, occupancy,
 *  assignment, charges and lifecycle. Availability counts THESE, not bookings. */
export type RoomStayId = string;
/** UUID string — a guest. */
export type GuestId = string;
/** bigint identity — a room type. */
export type RoomTypeId = number;
/** bigint identity — a physical room. */
export type RoomId = number;
/** bigint identity — a rate plan. */
export type RatePlanId = number;
/** Integer euro cents — money is never a float in this domain. */
export type Cents = number;
/** 'YYYY-MM-DD' — a calendar date, timezone-free (Europe/Paris civil date). */
export type IsoDate = string;
/** RFC3339 instant — an event timestamp. */
export type Instant = string;
/** `app_property.code` — one établissement: one SIRET, one Channex property,
 *  one invoice sequence, one taxe-de-séjour commune (ADR-0014). */
export type PropertyCode = string;
/** `app_tenant.code` — the OPERATOR, one personne morale per tenant. Nothing is
 *  ever shared across tenants; this is where Top-Level Hard Rule 3 is enforced. */
export type TenantCode = string;

// ── Reference data ───────────────────────────────────────────────────────────

export interface RoomType {
  id: RoomTypeId;
  /** The établissement it belongs to (U1). A category is ONE établissement's
   *  product: two properties' « DSTD » are different things. */
  propertyCode: PropertyCode;
  code: string; // DSTD/DSUP/TSTD/TSUP/FAM (D19) — also the site content slug
  labelFr: string;
  maxOccupancy: number;
  channexRoomTypeId: string | null; // P2 mapping, null until Channex onboarding
}

export type HkStatus = 'clean' | 'dirty' | 'inspected';

/** What KIND of lettable unit this is (D39/M10). A property of the UNIT, not of
 *  the property — an aparthotel mixes both, and a hotel may one day have a
 *  suite. Chooses the FR label (« chambre » / « appartement »), never a second
 *  code path (ADR-0014 §5). */
export type UnitKind = 'room' | 'studio' | 'apartment' | 'villa';

/** Which room of a multi-space unit a bed sits in. */
export type SpaceKind = 'bedroom' | 'living' | 'other';

export type KitchenKind = 'none' | 'kitchenette' | 'full';

/** One space's beds in a multi-space unit (`pms_room_bed`). Single-space units
 *  — every hotel room — have none of these and carry `Room.configOptions`
 *  instead; the two are exclusive, enforced in the database by a deferrable
 *  constraint trigger because neither half of that rule is expressible as a
 *  CHECK. (`BedSpace.bedConfig` below is a CONFIGURATION code, and stays: an
 *  apartment's bedroom really is made up as a `Double` or a `Twin`.) */
export interface BedSpace {
  spaceKind: SpaceKind;
  /** 1-based: « chambre 1 », « chambre 2 ». Not an ordering of beds. */
  spaceIndex: number;
  bedConfig: string;
  quantity: number;
}

/** A stored space — a `BedSpace` that knows which unit it belongs to. The write
 *  side uses `BedSpace` instead: the RPC already knows the unit it is writing,
 *  and sending `roomId` would be a second, disagreeable source for that fact. */
export interface RoomBed extends BedSpace {
  roomId: RoomId;
}

/** The apartment attributes of a unit. Every field is nullable because a hotel
 *  room does not have « zero bedrooms » — it has no opinion on the question,
 *  and 0 would be a claim. */
export interface UnitShape {
  unitKind: UnitKind;
  bedrooms: number | null;
  livingRooms: number | null;
  bathrooms: number | null;
  wcCount: number | null;
  kitchen: KitchenKind | null;
  surfaceSqm: number | null;
}

export interface Room extends UnitShape {
  id: RoomId;
  /** The établissement it belongs to (U1, PMS-UNIFIED-DESK-BRIEF §8). */
  propertyCode: PropertyCode;
  roomNumber: string;
  floor: number;
  roomTypeId: RoomTypeId | null; // operator-assigned; never guessed
  hkStatus: HkStatus;
  // Physical meta (Configuration Chambre.xlsx) — nullable, reference only.
  /** Every arrangement this room CAN be made up in (`pms_room_config_option`,
   *  0173). One entry = not convertible. EMPTY = a multi-space unit, whose beds
   *  are described space by space in `pms_room_bed` instead — that is the XOR. */
  configOptions: readonly string[];
  /** How the room is made up RIGHT NOW. NULL means NOT KNOWN — deliberately
   *  unlike `RoomStay.requestedBedConfig`'s NULL — because a room is always in
   *  SOME arrangement whether or not anyone recorded it (0157). There is no
   *  « default » to fall back on: nothing restores one, so the prevailing
   *  arrangement drifts with the season (0173). */
  currentBedConfig: string | null;
  /** Who stated it: « menage » (the étage), « desk » (a person confirming), or
   *  « derived » (not observed — the room has only one possible arrangement).
   *  NEVER « allocation »: a booking asking for a twin does not move a bed. */
  currentBedConfigSource: 'menage' | 'desk' | 'derived' | null;
  currentBedConfigAt: string | null;
  /** Which double this room holds, when it holds one — `Queen` or `King`.
   *  NULL = no double in any of its arrangements. Replaces the `King`
   *  pseudo-configuration of 0061. */
  doubleBedType: string | null;
  maxOccupancy: number | null;
  isSuperior: boolean;
  heatingZone: number | null;
  bathroomHeatingZone: number | null;
  view: string | null;
  hasShower: boolean;
  wcNotSeparated: boolean;
  remarks: string | null;
  /** Correction trace (room-edit brief E2) — who last changed the config, and
   *  when. Not a history: previous values are not kept. */
  updatedBy: string | null;
  updatedAt: string | null;
}

/** Everything `pms_update_room_config` writes, in one payload — the RPC is
 *  whole-row, so a partial patch would silently blank what it omits. */
export interface RoomConfigPatch {
  roomTypeId: RoomTypeId | null;
  /** The arrangements this room can be made up in. `null` = not managing them
   *  (a multi-space unit, whose beds arrive in `beds`); `[]` is refused by the
   *  RPC — a room has to be makeable somehow. */
  configOptions: string[] | null;
  /** `Queen` | `King` | null. Which double the room holds, not which
   *  arrangement it is in. */
  doubleBedType: string | null;
  maxOccupancy: number;
  isSuperior: boolean;
  heatingZone: number | null;
  bathroomHeatingZone: number | null;
  view: string | null;
  hasShower: boolean;
  wcNotSeparated: boolean;
  remarks: string | null;
  /** The unit shape, or null for « I am not managing it » — which is what a
   *  single-space unit means and what the pre-P1.6 client meant implicitly.
   *  It is null rather than a filled object deliberately: for these columns
   *  NULL is a legitimate value, so "absent" and "cleared" must stay
   *  distinguishable all the way to the RPC. */
  unit: UnitShape | null;
  /** The per-space beds, rewritten wholesale in the SAME transaction as the
   *  scalars (Hard Rule 2 — one save, never two). null = not managing spaces;
   *  `[]` = this unit has none. */
  beds: BedSpace[] | null;
  /** The unit's own category, when it owns one alone (M10 1:1) — relabel and
   *  re-capacity, in the same transaction as everything else. null = not
   *  managing the category, which is what a hotel room means: its category is
   *  shared, and the RPC refuses to edit a shared one from a single fiche. */
  type: RoomTypePatch | null;
}

/** What a 1:1 category exposes for editing. `code` is deliberately absent — it
 *  is the natural key every list shows and every operator cites, so renaming it
 *  is a different act from relabelling. `channexRoomTypeId` belongs to P2. */
export interface RoomTypePatch {
  labelFr: string;
  maxOccupancy: number;
}

/** Creating a room: identity + the few fields that make it render sensibly.
 *  Everything else is filled afterwards in the fiche (Hard Rule 4). */
export interface NewRoom {
  roomNumber: string;
  floor: number;
  roomTypeId: RoomTypeId | null; // may start untyped, as the seed did
  /** The arrangements the new room can take. null for a multi-space unit. */
  configOptions: string[] | null;
  doubleBedType: string | null;
  maxOccupancy: number;
  /** Omitted, the unit kind is resolved by the database from the
   *  établissement's own kind — so creating a unit in the apartments property
   *  does not require the form to know it is an apartment. */
  unit?: UnitShape | null;
  beds?: BedSpace[] | null;
  /** A category to CREATE with the unit, for the apartment case: M10 makes each
   *  apartment its own type, so a new one needs a category that does not exist
   *  yet. Mutually exclusive with `roomTypeId` — the RPC refuses both, since a
   *  caller sending each would have two answers to "which category". */
  newType?: { code: string; labelFr: string; maxOccupancy: number } | null;
}

/** A bed TYPE — the furniture, and nothing else (`pms_bed_type`, 0173).
 *  `Queen` rather than `Double` deliberately: `Double` names a CONFIGURATION,
 *  and one word for two concepts is the conflation 0173 removed. */
export interface BedType {
  code: string; // 'Queen' | 'King' | 'Single'
  labelFr: string;
  sleeps: number;
  sortOrder: number;
  active: boolean;
}

/** One ARRANGEMENT a room can be made up in (`pms_bed_config`, 0173).
 *  Reference data, not a constant: adding one is data entry, not a deploy
 *  (room-edit brief E1).
 *
 *  ⚠ A configuration is a REQUIREMENT, not a fixed multiset of furniture:
 *  `Double` is satisfied by a Queen or a King, and which one a given room
 *  holds is `Room.doubleBedType`. Convertibility is NOT here either — it is
 *  `Room.configOptions.length > 1`. Both used to be smuggled into this code's
 *  name (`King`, `Double - Twin`). */
export interface BedConfig {
  code: string; // 'Double' | 'Twin' | 'Triple' | '3 Singles' | 'Family'
  labelFr: string;
  layout: BedLayout;
  sortOrder: number;
  active: boolean;
}

/** The beds an arrangement calls for. A « double » here is satisfied by either
 *  bed type — see `BedConfig`. */
export interface BedLayout {
  doubles: number;
  singles: number;
  /** true = DERIVED (e.g. from capacity), not stated — drawn as a deduction,
   *  never asserted as fact (Hard Rule 1). */
  inferred: boolean;
}

export interface RatePlan {
  id: RatePlanId;
  /** The établissement it belongs to (U1, PMS-UNIFIED-DESK-BRIEF §8). */
  propertyCode: PropertyCode;
  code: string;
  labelFr: string;
  board: PmsBoard;
  /** The cancellation terms the plan is sold under (PB7a, `pms_cancellation_policy`).
   *  null = none assigned. Optional for a select that omits it. */
  cancellationPolicyId?: number | null;
  /** Whether this plan is mirrored to the channel manager at all (0108).
   *
   *  ⚠ CORRECTED 2026-08-22 (0109): this comment used to say B&B and
   *  demi-pension are direct-only and withheld from the OTAs. They are not —
   *  SiteMinder sells all three board plans through Booking.com, Expedia and
   *  Direct Booking — and the claim was the project's own reasoning credited to
   *  the operator in error. So « three plans become eighteen Channex plans » is
   *  the INTENDED outcome, not the hazard the flag guards against.
   *
   *  The flag stays because the question is real and a future plan may be
   *  direct-only, and because `provision()` reaches a live OTA where a mistake
   *  cannot be undone. */
  distributed: boolean;
  /** The plan this one takes its PRICE from (0110, R15).
   *
   *  A channel plan carries only its own restrictions — min LOS, stop sell —
   *  and inherits the parent's rate, mirroring Channex's derived rate plan
   *  (`rate_mode: derived` + `inherit_rate`), measured 2026-08-22 including
   *  propagation. NULL means this plan IS a parent and holds its own price.
   *
   *  The point is not that we keep several copies of a rate in step: a child
   *  has nowhere to hold one, so channel prices cannot diverge. */
  derivesFromId: RatePlanId | null;
  /** WHICH channel this plan carries, or null for the all-channel default
   *  (0108). Null is the norm — rates do not vary by channel (§11.6a). A
   *  non-null code is R15's shape, one plan per channel, taken because Channex
   *  scopes `close_out` per channel and min LOS not at all (§14.2). */
  channelCode: string | null;
  // NO channexRatePlanId — see ChannexRatePlanLink below. A Channex rate plan
  // belongs to ONE room type, so the mapping is per (plan × type) and lives in
  // its own table (0102).
}

/**
 * WHICH CHANNEX RATE PLAN is our rate plan FOR ONE ROOM TYPE (0102).
 *
 * Channex requires a rate plan to belong to exactly one room type (vendor docs,
 * verified 2026-08-17), so our single « Tarif standard » becomes six Channex
 * rate plans at the hotel and three at the apartments. `RatePlan` therefore
 * carries NO `channexRatePlanId` — one field could hold one id, which is what
 * made `pushAri` push five room types' restrictions onto the wrong plan.
 *
 * Environment-specific: these are SANDBOX ids until the production cutover
 * re-provisions them.
 */
export interface ChannexRatePlanLink {
  ratePlanId: RatePlanId;
  roomTypeId: RoomTypeId;
  channexRatePlanId: string;
  /** EVERY occupancy the plan is sold at, the primary included (0104). */
  occupancyOptions: ChannexOccupancyOption[];
}

/**
 * ONE OCCUPANCY OF A CHANNEX RATE PLAN — and the unit a rate is actually
 * addressed to (0104, measured against the sandbox 2026-08-19).
 *
 * A Channex rate plan is not one sellable thing but one per occupancy, each
 * with its own id; the plan's own id EQUALS the primary option's. A rate pushed
 * to the plan reaches the primary occupancy alone and the rest stay unset for
 * ever — and unset is not free, it is « no price », since `rate: 0` is refused.
 *
 * Neither shortcut works: a zero-delta derivation is accepted and ignored, and
 * an `occupancy` field on a restriction is accepted, queued, and applied to the
 * primary anyway. **An occupancy can only be addressed by its own id**, which
 * is why these are stored rather than derived at push time.
 */
export interface ChannexOccupancyOption {
  occupancy: number;
  channexOptionId: string;
}

/** One cell of the ARI grid: price/restriction for a plan × type × day. */
export interface Rate {
  ratePlanId: RatePlanId;
  roomTypeId: RoomTypeId;
  date: IsoDate;
  priceCents: Cents;
  minStay: number;
  closed: boolean;
  /**
   * TRUE when a human set this price in « Inventaire » rather than the grid
   * generator deriving it (0117). The generator must never overwrite one, and
   * must report how many it skipped (R4).
   *
   * OPTIONAL, and that is the honest shape rather than laziness: a `Rate` is
   * both a row READ from the grid — where the marker is a fact — and a row
   * PROPOSED by `generateRates`, where it does not exist yet because nothing
   * has been stored. Making it required would force the pure generator to
   * assert a provenance it is in no position to know.
   */
  handAdjusted?: boolean;
  /**
   * CC1 (PMS-CHANNEX-CERTIFICATION-BRIEF §3.5) — the three restrictions beyond
   * min stay and stop sell. Same semantics as the channel's:
   *   closedToArrival   — no stay may START on this date (CTA);
   *   closedToDeparture — no stay may END on this date: the departure day,
   *                       which is not itself a night of the stay (CTD);
   *   maxStay           — the longest stay that may start here; null = none.
   *
   * OPTIONAL, read as « nothing restricted » when absent (`rateRestrictions`).
   * Absent is also what a row read from a database without the columns looks
   * like — the window between this code deploying and the CC1 migration
   * reaching that database — and the write path omits an absent field rather
   * than sending a column that may not exist yet.
   */
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
  maxStay?: number | null;
}

/** A rate's CC1 restrictions with absent read as « nothing restricted ». */
export function rateRestrictions(r: Pick<Rate, 'closedToArrival' | 'closedToDeparture' | 'maxStay'>): {
  closedToArrival: boolean;
  closedToDeparture: boolean;
  maxStay: number | null;
} {
  return {
    closedToArrival: r.closedToArrival ?? false,
    closedToDeparture: r.closedToDeparture ?? false,
    maxStay: r.maxStay ?? null,
  };
}

// ── Guest ────────────────────────────────────────────────────────────────────

export type GuestLang = 'fr' | 'en' | 'es' | 'de';

export interface Guest {
  id: GuestId;
  lastName: string;
  firstName: string;
  email: string | null;
  phone: string | null;
  lang: GuestLang;
  notes: string | null;
}

/** New-guest payload (no id yet). */
export type GuestDraft = Omit<Guest, 'id'>;

// ── The cardex: search and fiche client (PMS-CARDEX-BRIEF §6.1) ──────────────

/** B2C or B2B — one table and one id space for both (operator, N4). */
export type CustomerType = 'particulier' | 'societe';

/** A B2B sous-type — reference DATA the operator extends (`pms_customer_subtype`). */
export interface CustomerSubtype {
  code: string;
  labelFr: string;
  active: boolean;
  sortOrder: number;
}

/** What the desk asks the search. An empty `query` is legal only with a
 *  filter — the RPC refuses it otherwise, rather than returning everyone. */
export interface GuestSearch {
  query: string;
  customerType?: CustomerType | null;
  /** « Société sans SIREN » — D6's readiness report. */
  missingSiren?: boolean;
  /** « Consentement requis » — pass 2 (D9): loaded ahead of the consent
   *  campaign, operator 2026-09-29. */
  consentRequired?: boolean;
  /** At most 50. */
  limit?: number;
}

/** One client in a search result. */
export interface GuestSummary {
  id: GuestId;
  lastName: string;
  firstName: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  city: string | null;
  countryCode: string | null;
  lang: GuestLang;
  customerType: CustomerType;
  customerSubtype: string | null;
  siren: string | null;
  /** Vega's client number — the seed's key, and a search term. */
  sourceVegaRef: string | null;
  /** Carried from Vega by the cardex load (`cardexLoadedAt` set). */
  cardex: boolean;
  /** « Tenu par Vega » (S3): name and contact details are Vega's until the
   *  établissement goes live, and the database refuses a change to them. */
  vegaHeld: boolean;
  /** 1 = seen in the last 3 years; 2 = 3–5 years ago, « consentement
   *  requis » (D9); null = not carried from Vega's cardex. */
  cardexPass: 1 | 2 | null;
  /** The latest activity Vega held when the client was loaded — a stay's
   *  departure or a booking's arrival, so it can be in the future. What the
   *  pass was computed from; null for a client with none on record. */
  cardexLastActivity: IsoDate | null;
}

export interface GuestSearchResult {
  hits: GuestSummary[];
  /** More matched than were returned — the desk should narrow the search. */
  truncated: boolean;
}

/** The fiche client — everything the sheet shows. */
export interface GuestSheet extends GuestSummary {
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  notes: string | null;
  vatNumber: string | null;
  cardexLoadedAt: Instant | null;
  updatedAt: Instant;
}

/** The fields a fiche client save may carry. Absent = unchanged, null = clear.
 *  The identity half is refused on a `vegaHeld` client when it changes. */
export interface GuestSheetPatch {
  lastName?: string;
  firstName?: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
  lang?: GuestLang;
  notes?: string | null;
  customerType?: CustomerType;
  customerSubtype?: string | null;
  siren?: string | null;
  vatNumber?: string | null;
}

// ── Reservation aggregate ────────────────────────────────────────────────────

// ── Charges — what a stay costs (charge brief §4) ────────────────────────────
//
// `pms_reservation_night` was merged into `pms_charge` on 2026-08-14 (Q1), so a
// stay has ONE line table, one total and one void rule. `ReservationNight` is
// gone with it: a room night is now a `ChargeDay` whose `kind` is `'room'`, and
// keeping a second shape for the same row would let the two drift.

/** bigint identity — a catalogue product. */
export type ProductId = number;
/** bigint identity — one charge line. */
export type ChargeDayId = number;

/** `room` = a night · `product` = an extra · `tax` = taxe de séjour.
 *
 *  Load-bearing, not descriptive. The tax is COLLECTED and not earned, so it
 *  must never be counted as revenue, and availability (if it ever reads charges
 *  at all) must read `room` only — a product dated inside a stay must not make
 *  a unit look sold. */
export type ChargeKind = 'room' | 'product' | 'tax';

/** A product's recurrence rule, Amenitiz's semantics verbatim.
 *
 *  `each_night` and `each_morning` are NOT the same count shifted by one: the
 *  first excludes the departure day, the second excludes the arrival day, so
 *  they select different dates for the same number of lines. */
export type AppliesTo = 'arrival' | 'departure' | 'each_night' | 'each_morning';

/** A catalogue extra — « animal 5 €/nuit », parking, late checkout — and, when
 *  `isTax`, the taxe-de-séjour tariff. Per PROPERTY: the apartments and the
 *  hotel do not sell the same extras. */
export interface Product {
  id: ProductId;
  /** Its établissement (U3) — a catalogue is one établissement's. */
  propertyCode: PropertyCode;
  code: string;
  labelFr: string;
  descriptionFr: string | null;
  kind: 'service' | 'produit';
  emoji: string | null;
  appliesTo: AppliesTo;
  /** A UNIT is an article or a person — never a night (the nights come from
   *  `appliesTo`). Q5: per-person products exist. */
  perPerson: boolean;
  priceCents: Cents;
  /** Percentage. On the PRODUCT, never on the line: on the line every writer
   *  would have to supply it, and one eventually would not. `null` only for a
   *  tax, which bears none. */
  tvaRate: number | null;
  /** Configured as a product, ACCOUNTED as a tax (charge brief §4.3). */
  isTax: boolean;
  /** The difference between « offered » and « always billed ». */
  autoApply: boolean;
  /** The desk may set a quantity — « 2 animaux ». */
  manualQty: boolean;
  active: boolean;
  sortOrder: number;
  /** A meal the board counts (PDJ, PANIER, DINER_DP…): switched per room and
   *  per person, never ticked as an extra (group edit §2.6). null otherwise. */
  boardPrestation?: MealPrestation | null;
  /** Collected for someone else (SCI mandate, tips, débours) — a POSTING
   *  vehicle, never chosen at booking (in-stay charges brief Q2). */
  isPassthrough?: boolean;
  /** The age band whose price the published rate profile writes here
   *  (PMS-PHONE-BOOKING-BRIEF §4.2) — the price is then read-only in the
   *  catalogue. null for every other product. */
  profileBand?: ChildMealBand | null;
}

/**
 * A catalogue product as the operator TYPES it — `id` absent creates.
 *
 * `code` is the stable key (unique per établissement) and `labelFr` is what a
 * guest reads; they are deliberately separate, because renaming « Petit-déj »
 * to « Petit-déjeuner » must not orphan the charges that named the code.
 *
 * There is no `ProductDraft.active`: retiring is its own act (`setProductActive`)
 * rather than a field on a form, so « I edited the price » and « I withdrew it
 * from sale » cannot be the same accidental save.
 */
export interface ProductDraft {
  id?: ProductId;
  code: string;
  labelFr: string;
  descriptionFr: string | null;
  kind: Product['kind'];
  emoji: string | null;
  appliesTo: AppliesTo;
  perPerson: boolean;
  priceCents: Cents;
  /** REQUIRED for anything sellable and FORBIDDEN on a tax — the database
   *  enforces both (`pms_product_sale_states_tva`, `pms_product_tax_bears_no_tva`).
   *  A levy collected for a commune bears no TVA because it is not a sale. */
  tvaRate: number | null;
  /** A taxe de séjour tariff rather than something we sell. Forces per-person,
   *  per-night and no TVA — the shape a communal levy legally has. */
  isTax: boolean;
  autoApply: boolean;
  manualQty: boolean;
  sortOrder: number;
}

/** bigint identity — one agreed charge (the range layer). */
export type ChargeId = number;
/** bigint identity — one folio: a payer within a booking. */
export type FolioId = number;

/**
 * How a booking's folios are LAID OUT when its charges are routed (0098).
 *
 * A seeding rule, not a mode anything reads afterwards — the operator's binding
 * clause was « in any case when collecting payments the system must be flexible
 * enough to split/consolidate rooms/extras », so every charge stays movable no
 * matter which of these laid it down.
 *
 * - `booker_rooms` — the booker settles the rooms and the taxe de séjour on
 *   them; each logement settles its own extras. The default and the common case.
 * - `booker_all` — one folio for everything. Groups and tour organisers.
 * - `room_all` — each logement settles everything of its own.
 */
export type BillingMode = 'booker_rooms' | 'booker_all' | 'room_all';

/**
 * WHAT IS AGREED — one row of the desk's grid (0096).
 *
 * A product (or a room night, or the levy), a quantity, a unit price, and a
 * half-open range. This is the layer a folio PRINTS and a person edits; it
 * expands by trigger into `ChargeDay` rows, which are the layer an invoice is
 * built from and a communal return is audited against.
 *
 * `quantity` is units — articles or persons — and NEVER nights: the nights are
 * the range. « 2 animaux for 3 nights » is quantity 2 over a 3-day range.
 */
export interface Charge {
  id: ChargeId;
  reservationId: ReservationId;
  /** What this is FOR. `null` means the BOOKING's own — a folio-level extra
   *  such as a garage a family takes across two rooms. Structural and stable:
   *  it does not move because someone else offers to pay. */
  roomStayId: RoomStayId | null;
  /** WHO PAYS. Independent of `roomStayId` — a charge for room 2 is very often
   *  on the booker's folio, which is the operator's most common configuration. */
  folioId: FolioId;
  kind: ChargeKind;
  productId: ProductId | null;
  ratePlanId: RatePlanId | null;
  labelFr: string;
  quantity: number;
  unitCents: Cents;
  startDate: IsoDate;
  /** EXCLUSIVE. A one-day extra on the 27th is [27, 28). */
  endDate: IsoDate;
  tvaRate: number | null;
  personsTaxable: number | null;
  personsExempt: number | null;
  /** A line a person typed, which no automatic source may overwrite — it is
   *  what the occupancy re-derivation and any future rate refresh skip. */
  isManual: boolean;
  voidedAt: Instant | null;
}

/** WHO PAYS: one folio per payer within a booking (0098).
 *
 *  `roomStayId === null` is the BOOKER's folio, which every booking has and
 *  which is where an unrouted charge lands. */
export interface Folio {
  id: FolioId;
  reservationId: ReservationId;
  roomStayId: RoomStayId | null;
  labelFr: string;
  payerGuestId: GuestId | null;
  /** An occupant we know only by name — Channex gives one `customer` per
   *  booking and a name per room. */
  payerName: string | null;
}

/**
 * WHO IS ACTUALLY IN A LOGEMENT, for a span of nights (0097).
 *
 * The primitive the taxe de séjour is DERIVED from and never typed. Distinct
 * from `RoomStay.adults/children`, which is what was BOOKED: they are seeded to
 * agree and are allowed to diverge, and the divergence is the point — « 2
 * adults for the first 2 nights and just one the following 2, one having left ».
 *
 * A logement's spans TILE `[checkinDate, checkoutDate)` exactly: no gap, no
 * overlap. A gap would be a night declaring nobody to the commune.
 */
export interface OccupancySpan {
  id: number;
  roomStayId: RoomStayId;
  startDate: IsoDate;
  /** EXCLUSIVE. */
  endDate: IsoDate;
  /** 18 and over — the exemption's boundary, so this is what is declared. */
  adults: number;
  /** Under 18: exempt from the levy, full occupants for everything else. */
  children: number;
  /** Under 2: excluded from EVERY multiplier (D1). */
  infants: number;
}

/** A person's age band (PMS-GROUP-EDIT-BRIEF Q3). `child_unknown` is the
 *  BACKFILL band — a child counted before ages existed: a minor for the taxe de
 *  séjour, billing no age-band product until the desk sets a real one. */
export type AgeBand =
  | 'adult'
  | 'child_13_17'
  | 'child_9_12'
  | 'child_6_8'
  | 'child_3_5'
  | 'child_unknown'
  | 'infant';

/** The three meals the desk switches and the board counts. */
export type MealPrestation = 'breakfast' | 'packed_lunch' | 'dinner';

/**
 * ONE PERSON IN A LOGEMENT, named or not — the primitive since group edit
 * step 1 (`pms_stay_person`). The logement's counts and its occupancy spans are
 * counts of these. Dates are the person's own: `[startDate, endDate)`, the
 * logement's unless they arrive late or leave early.
 */
export interface StayPerson {
  id: string;
  roomStayId: RoomStayId;
  /** Order in the room, 1..n. */
  position: number;
  /** null = « Adulte 2 » until the rooming list names them. */
  name: string | null;
  ageBand: AgeBand;
  startDate: IsoDate;
  /** EXCLUSIVE — the checkout morning. */
  endDate: IsoDate;
}

/**
 * An EXCEPTION to what a person takes (`pms_stay_person_meal`). No row = the
 * default: included by the type de séjour, or switched on for the whole room.
 * `serviceDate` is the day the meal is eaten — a breakfast or a packed lunch
 * the morning after a night, a dinner on the night.
 */
export interface StayPersonMeal {
  personId: string;
  serviceDate: IsoDate;
  prestation: MealPrestation;
  taken: boolean;
}

/** The 14 regulated allergens (Annex II of Règlement (UE) 1169/2011, FR Décret
 *  2015-447) as the database stores them — `pms_allergen_codes()`. */
export type Allergen =
  | 'gluten'
  | 'crustaceans'
  | 'eggs'
  | 'fish'
  | 'peanuts'
  | 'soybeans'
  | 'milk'
  | 'nuts'
  | 'celery'
  | 'mustard'
  | 'sesame'
  | 'sulphites'
  | 'lupin'
  | 'molluscs';

/**
 * A person's NEEDS AND ALLERGENS (`pms_stay_person_needs`, group edit step 5).
 *
 * HEALTH DATA (RGPD art. 9): held to serve the guest safely, readable only with
 * `pms.needs.read`, erased 30 days after the person's departure. A person with
 * nothing declared has no row.
 */
export interface StayPersonNeeds {
  personId: string;
  /** Free text — allergies, diet, mobility — in the words of whoever gave it. */
  needs: string | null;
  allergens: Allergen[];
}

/** A person as the write command takes it (`rooms[].persons`, step 3). The
 *  list is the room's COMPLETE set; `meals`, when present, is the person's
 *  complete set of exceptions (absent = keep them). `needs` and `allergens`
 *  (step 5) follow the same rule, and the database refuses them from a caller
 *  without `pms.needs.read` — so a desk that cannot see them never sends them. */
export interface StayPersonInput {
  id?: string | null;
  name: string | null;
  ageBand: AgeBand;
  startDate: IsoDate;
  endDate: IsoDate;
  meals?: Omit<StayPersonMeal, 'personId'>[];
  needs?: string | null;
  allergens?: Allergen[];
}

/** One span as the desk types it. The complete set is sent at once, because a
 *  partial set is a stay with a hole in it. */
export interface OccupancySpanInput {
  startDate: IsoDate;
  endDate: IsoDate;
  adults: number;
  children: number;
  infants: number;
}

/**
 * What an OTA sold alongside a room (0094) — Channex `services[]`, per logement.
 *
 * NOT a charge, deliberately: the OTA sends no TVA rate, and writing one as a
 * charge would book an invented fiscal figure into the TVA return. It appears
 * on the folio as information — « vendu par l'OTA » — until the operator maps
 * it onto a catalogue product.
 */
export interface OtaService {
  id: number;
  roomStayId: RoomStayId;
  /** The channel's own wording, kept verbatim. */
  name: string;
  priceMode: string;
  persons: number | null;
  nights: number | null;
  totalCents: Cents;
  /** Null until someone maps it onto our catalogue. */
  productId: ProductId | null;
}

/** One dated line of what a stay costs. Corrected by VOID-AND-APPEND, never
 *  updated in place (Hard Rule 5: legal obligation + audit trail) — which is
 *  why there is no `ChargeDayPatch` anywhere in this package.
 *
 *  The EVIDENCE layer. What prints on a folio is the `Charge` above it; these
 *  are what an invoice is built from and what a contrôle reads. */
/** Who wrote a charge (`pms_charge.origin`). */
export type ChargeOrigin = 'agreed' | 'derived' | 'seed' | 'posted';

export interface ChargeDay {
  /** The agreement this is one day OF. */
  chargeId: ChargeId;
  /** Who wrote the charge this day belongs to (20261017100100): agreed by the
   *  booking, derived from persons, seeded from Vega, or posted. Absent when
   *  the read did not embed it. A form reloading a booking's requests must
   *  skip `derived` lines — they come back from the persons on every save. */
  origin?: ChargeOrigin;
  id: ChargeDayId;
  reservationId: ReservationId;
  /** WHICH ROOM this line is for (0090). The booking is still named above so
   *  the folio can be read whole; this is what makes a per-room bill possible
   *  — brief §5's « what a charge is FOR » (structural, stored), as distinct
   *  from who PAYS it (a rule today, a nullable column at invoice time).
   *
   *  NULL is NOT « unmigrated »: the backfill left none, and a `kind='room'`
   *  line without one is refused outright (`pms_charge_room_names_its_stay`).
   *  It is reserved for a future folio-level extra belonging to the BOOKING
   *  rather than to any one room — so treat null as « the whole booking », not
   *  as missing data. */
  roomStayId: RoomStayId | null;
  date: IsoDate;
  kind: ChargeKind;
  productId: ProductId | null;
  ratePlanId: RatePlanId | null;
  labelFr: string;
  /** Units: articles, or persons. Never nights. */
  quantity: number;
  unitCents: Cents;
  /** quantity × unit, STORED — a folio must not re-derive what it prints. */
  amountCents: Cents;
  tvaRate: number | null;
  /** Tax lines only: the person count the levy was computed from. A tax line
   *  that cannot say « 3 adultes × 4 nuits » is a number nobody can check
   *  against the commune's return (§3.3a). */
  personsTaxable: number | null;
  /** Tax lines only: the exempt (under-18) count, carried because the état
   *  déclaratif some communes issue asks for it beside the taxable one. */
  personsExempt: number | null;
  /** A line a person typed. No automatic source may overwrite it (§4.1). */
  isManual: boolean;
  voidedAt: Instant | null;
}

/** One night of the price vector the write path takes. The desk types these
 *  today; when the rate engine lands they come from `pms_rate` instead — the
 *  same shape either way, which is the point of it being a vector (§3.1). */
export interface NightPrice {
  date: IsoDate;
  priceCents: Cents;
  /** How many units the night bills at `priceCents` — ABSENT means one room.
   *  A group Vega prices PER PERSON carries the room's heads (« DEMI PENSION
   *  GROUPE » 2 × 75 €); a group line Vega could not spread over its rooms
   *  carries several rooms on one logement (VEGA-SEED-BRIEF §7.3d). Read it
   *  with `pmsRoomLineUnits`. Never nights. */
  quantity?: number;
}

/** An extra asked for on a stay. `quantity` is what the DESK asked for, before
 *  any per-person multiplication — the writer does that from the product. */
export interface ProductRequest {
  productId: ProductId;
  quantity: number;
}

export interface Reservation {
  id: ReservationId;
  status: PmsReservationStatus;
  noShow: boolean;
  guestId: GuestId;
  roomTypeId: RoomTypeId;
  roomId: RoomId | null; // null until assigned
  checkinDate: IsoDate;
  checkoutDate: IsoDate; // exclusive
  adults: number;
  children: number;
  /** HOW the booking reached us. One of three orthogonal facts (0137): the
   *  mechanism here, the OTA in `channel`, the counterparty in
   *  `bookingPartyCode`. Vega collapses all three into one pointer and loses
   *  the OTA behind its channel manager as a result. */
  source: PmsSource;
  channel: string | null; // WHICH OTA — from the booking message, not the CM (0137)
  channelRef: string | null; // OTA confirmation number
  /** WHO booked, where that is a named counterparty — a tour operator, a
   *  corporate account. NULL is the ordinary case: a guest booking for
   *  themselves is not a party. `pms_booking_party.code` (0137). */
  bookingPartyCode: string | null;
  /** `individual` | `group` — a commercial fact, not a room count
   *  (PMS-GROUPS-BRIEF §4). The group header IS the booking. */
  kind: 'individual' | 'group';
  /** The label staff use for a group (« DUBLANCHE »); null for an individual. */
  groupName: string | null;
  /** Why it was cancelled — `pms_cancellation_reason.code` (0138). Non-null
   *  only on a cancelled booking, and NULL on one is legitimate (nobody was
   *  asked). Reports counting cancellations must exclude the reasons whose
   *  `isCorrection` is true, or they read roughly double the truth. */
  cancellationReasonCode: string | null;
  channexBookingId: string | null; // P2 webhook idempotency key
  guarantee: PmsGuarantee;
  cardVaultRef: string | null; // P2.5 vault POINTER — never a PAN (O7)
  vadAmountCents: Cents | null;
  /** Arrhes by bank transfer (`transfer_pending`): the day the transfer should have arrived —
   *  the desk chases it once past. null for any other guarantee; ABSENT when the read did not carry it. */
  transferDueOn?: IsoDate | null;
  totalCents: Cents;
  notes: string | null;
  /** The channel's own words for the booking, « Genius » and all — REPLACED
   *  wholesale on each revision and edited by nobody (`20260929040100`, VM5 Q4). Evidence,
   *  never a staff note: that is `notes`. Read-only here; no write path sends it. */
  sourceText: string | null;
  /** The arrival window the channel gave, ONE per booking (VM5 Q2) — Postgres
   *  `time`, so « 18:00:00 ». Either end may be null on its own. */
  arrivalWindowFrom: string | null;
  arrivalWindowTo: string | null;
  /** Vega's booking number when this booking CAME FROM Vega's seed (`source_vega_resa`), else
   *  null. A seeded booking's room and dates are put back by the seed within 30 minutes until
   *  go-live, so the Planning does not let the desk drag it. Optional so a read before the
   *  column was selected maps to « not Vega's ». */
  sourceVegaResa?: string | null;
  createdAt: Instant;
  updatedAt: Instant;
}

/** Reservation joined with the shapes the desk/list views need to render. */
export interface ReservationView extends Reservation {
  /** The établissement it belongs to (U1, PMS-UNIFIED-DESK-BRIEF §8). */
  propertyCode: PropertyCode;
  guest: Guest | null;
  roomNumber: string | null;
}

/**
 * ONE ROOM of a booking — « un logement ». Mirrors `pms_room_stay` (0086-0091).
 *
 * Read this and not the booking for anything ROOM-shaped: which unit, which
 * dates, who is in it, whether they have arrived. `Reservation`'s own
 * `roomId` / `checkinDate` / `status` are DERIVED summaries over these
 * (`pms_derive_reservation`) — a stand-in kept for the list and the filters,
 * and never a fact to reason from once logements are in hand.
 *
 * The EN identifiers say `room` / `roomStay`, never « logement » — the word is
 * the staff-facing label only (D4), and migration 0080's « Logement déjà
 * occupé » already spends it on the physical unit.
 */
export interface RoomStay {
  id: RoomStayId;
  reservationId: ReservationId;
  status: PmsReservationStatus;
  roomTypeId: RoomTypeId;
  /** The category the logement was SOLD as — for a Vega-seeded one, its plan
   *  product's category, which can differ from `roomTypeId` (the seed sets that
   *  to the type of the room it is in) when the desk upgraded the group in Vega.
   *  DISPLAY and the way back down only: availability and the ARI push still
   *  key on `roomTypeId`. null = not known (read `soldRoomTypeId ?? roomTypeId`);
   *  ABSENT when the read did not carry it. */
  soldRoomTypeId?: RoomTypeId | null;
  /** Seeded from a Vega stay. Its `soldRoomTypeId` is then the ONLY word on what it
   *  was sold as: null there means Vega's plan has no line for this logement, and
   *  `roomTypeId` (the allocated room's type) must not stand in for it. ABSENT
   *  when the read did not carry it. */
  fromVega?: boolean;
  /** The cancellation terms the logement was SOLD under (PB7b): its plan's policy,
   *  copied at sale and never changed by a later edit of the policy. null = sold
   *  before PB7b, or under a plan with no policy; ABSENT when the read did not
   *  carry it. */
  cancellationTerms?: SoldTerms | null;
  roomId: RoomId | null; // null until assigned
  ratePlanId: RatePlanId;
  checkinDate: IsoDate;
  checkoutDate: IsoDate; // exclusive
  /** NULL means « the booker » — an OTA gives one customer per booking, and
   *  this is deliberately a NAME, not a guest reference (0086). */
  occupantName: string | null;
  adults: number;
  children: number;
  /** Recorded, then excluded from every multiplier (D1). Housekeeping's only:
   *  > 0 means a cot to place. Must NOT be added to the per-person count. */
  infants: number;
  /** The bed CONFIGURATION the guest asked for (`pms_bed_config.code`, 0136).
   *  NULL means « no request — as the room is », deliberately unlike
   *  `Room.currentBedConfig`'s NULL (« not known »). Read-only here. */
  requestedBedConfig: string | null;
  /** When this logement stopped holding inventory. NULL = live. Tied to
   *  `status === 'cancelled'` by a CHECK in the database (0091). */
  cancelledAt: Instant | null;
  otaUniqueId: string | null;
  /** The room's meal switches (group edit step 2): taken by every person
   *  present unless a person's exception says otherwise. ABSENT when the read
   *  did not carry them (the rack window), which is not the same as none. */
  meals?: readonly MealPrestation[];
}

/** `allowOtherCategory`: the unit may be of a category other than the stay's — the Planning's
 *  confirmed drop (ADR-0028). The stay then takes the unit's category for availability and keeps
 *  what was bought as its sold category. Refused for a Vega-seeded booking, by the RPC. */
export interface AssignRoomStayOptions {
  allowOtherCategory?: boolean;
}

/** A room stay with what the rack and the list need to draw it. */
export interface RoomStayView extends RoomStay {
  /** The établissement it belongs to (U1, PMS-UNIFIED-DESK-BRIEF §8). */
  propertyCode: PropertyCode;
  roomNumber: string | null;
}

/**
 * What the rack reads for a period [from, to) — `pms_rack_window`.
 *
 * Not « the stays in the period », which is only the first of four parts:
 *   1. every LIVE logement with a night in the period;
 *   2. the other live logements of those bookings (siblings);
 *   3. per unit, the nearest occupying stay before the period and after it;
 *   4. the bookings of all of the above.
 *
 * Part 3 is what lets `proposeRoomAssignment` rank a stay lying INSIDE the
 * period exactly as it would with every stay loaded: a unit's nearest
 * neighbour to that stay is either in the period or is that frontier stay. A
 * stay reaching outside the period is not covered — read a wider one first.
 */
export interface RackWindow {
  stays: RoomStayView[];
  reservations: ReservationView[];
}

/**
 * ONE BOOKING FOUND BY THE PLANNING'S SEARCH BOX — `pms_reservation_search`.
 *
 * Just what the desk needs to recognise a booking and to reach it: not a
 * `ReservationView`, which carries the whole booking and is read when it is
 * opened. `number` is Vega's reservation number (« 023394 ») — null for a booking
 * born here. `stayId` is the EARLIEST LIVE logement, the one the planning
 * selects; null when every logement was released.
 */
export interface BookingSearchHit {
  reservationId: ReservationId;
  propertyCode: PropertyCode;
  number: string | null;
  status: PmsReservationStatus;
  /** « NOM Prénom » of the booker; null without `pms.guest.read`, or when unnamed. */
  guestName: string | null;
  groupName: string | null;
  channel: string | null;
  channelRef: string | null;
  checkinDate: IsoDate;
  checkoutDate: IsoDate; // exclusive
  /** Room numbers of the live logements that have a unit, checkin-ordered. */
  rooms: readonly string[];
  /** Live logements, with or without a unit. */
  roomCount: number;
  stayId: RoomStayId | null;
}

export interface BookingSearchResult {
  hits: readonly BookingSearchHit[];
  /** There were more matches than the limit: say so, do not imply these are all. */
  more: boolean;
}

/** Append-only audit row. Mirrors pms_reservation_event. */
export interface ReservationEvent {
  id: number;
  reservationId: ReservationId;
  event: string;
  payload: Record<string, unknown>;
  actor: string | null;
  /** « Marie D. » — the person at the keyboard (ADR-0026), null when none was stamped. */
  operatorLabel: string | null;
  createdAt: Instant;
}

/** Out-of-order / out-of-service block on a physical room. */
export interface RoomBlock {
  id: number;
  roomId: RoomId;
  startDate: IsoDate;
  endDate: IsoDate; // exclusive
  reason: string | null;
}

// ── Availability (derived — never a maintained counter) ──────────────────────

export interface AvailabilityCell {
  day: IsoDate;
  roomTypeId: RoomTypeId;
  code: string;
  total: number;
  occupied: number;
  blocked: number;
  available: number; // may be negative → overbooking alert
}

// ── Reception payment log (P3-T, PMS-TERMINAL-LINK-BRIEF §8.6/§8.7) ──────────
// The desk's replacement for the paper payment log. `pms_reception_payment`
// (0187/0189) — append-only, one row per movement, corrected by a new
// offsetting row rather than an edit.

/** bigint identity — a card terminal (`pms_terminal`). */
export type TerminalId = number;
/** bigint identity — a declared "about to charge X" (`pms_terminal_intent`). */
export type TerminalIntentId = number;
/** bigint identity — the terminal's answer (`pms_terminal_transaction`). */
export type TerminalTransactionId = number;
/** bigint identity — one row of the reception payment log. */
export type ReceptionPaymentId = number;

/** 'void' cancels a suspense row (0189) — it carries no money and always
 *  references what it cancels via `settlesSuspenseId`. 'transfer' (virement)
 *  and 'vad' (paid on the bank's hosted page) are typed, like cash — only
 *  'card' comes from the TPE (20261024100100: how arrhes mostly arrive). */
export type TenderType = 'card' | 'cash' | 'cheque' | 'ancv' | 'transfer' | 'vad' | 'suspense' | 'void';

export type ReceptionPaymentStatus = 'captured' | 'deferred';

/** A physical card terminal the desk can drive (today: the one A920). */
export interface Terminal {
  id: TerminalId;
  label: string;
  ipAddress: string;
  port: number;
  /** Last poll of the LAN bridge (stamped ≤ every 15 s). The desk offers
   *  « 1 Carte » only while this is recent (§8.8q). */
  bridgeSeenAt: Instant | null;
}

/** One row of the desk's payment journal, as logged. */
export interface ReceptionPayment {
  id: ReceptionPaymentId;
  resa: string | null;
  roomNumber: string | null;
  tenderType: TenderType;
  /** For suspense: what the guest SHOULD pay. For everything else: what was
   *  collected. Zero only for a void row. */
  amountCents: Cents;
  status: ReceptionPaymentStatus;
  terminalTransactionId: TerminalTransactionId | null;
  settlesSuspenseId: ReceptionPaymentId | null;
  capturedAt: Instant;
  vegaKeyed: boolean;
  vegaKeyedAt: Instant | null;
}

/** The room field's answer — today's in-house stay for a room number, or
 *  nothing (a normal case, not an error — see `pms_lookup_room_stay`). */
export interface RoomStayLookup {
  resa: string;
  /** Booker name for an individual, group name for a group booking. */
  partyLabel: string;
  checkin: IsoDate;
  checkout: IsoDate;
  /** Where the stay is — the payment and any card intent are filed HERE,
   *  not under the screen's scope (§8.8n). */
  propertyCode: PropertyCode;
}

/** The terminal's answer to one intent — what the card flow waits for. */
export interface TerminalTransactionResult {
  id: TerminalTransactionId;
  intentId: TerminalIntentId;
  amountCents: Cents;
  /** authorised / offline_approved = money moved; refused = card refused;
   *  not_done = the terminal did not carry it out, no debit (retry safe);
   *  unknown = anything else — check the TPE before retrying (§8.8q). */
  status: 'authorised' | 'offline_approved' | 'refused' | 'not_done' | 'unknown';
  authorisationRef: string | null;
  cardMasked: string | null;
}

/** The write behind `pms_record_reception_payment`. For `tenderType: 'card'`,
 *  `terminalTransactionId` is required and `amountCents` is IGNORED — the
 *  amount comes from the linked `pms_terminal_transaction`, never from the
 *  caller (§8.7's own rule, enforced again by the RPC and the table CHECK).
 *  For every other tender, `amountCents` is required and
 *  `terminalTransactionId` must be absent. */
export interface RecordReceptionPaymentInput {
  tenderType: Exclude<TenderType, 'void'>;
  roomNumber?: string | null;
  resa?: string | null;
  amountCents?: Cents | null;
  terminalTransactionId?: TerminalTransactionId | null;
  settlesSuspenseId?: ReceptionPaymentId | null;
  /** The booking's property (from the lookup). Omitted = the screen's scope. */
  propertyCode?: PropertyCode | null;
  /** The Encaissement tab it was taken on — the shift (§8.8p). Null: another tab. */
  takenOn?: DeskShift | null;
}

export interface CreateTerminalIntentInput {
  terminalId: TerminalId;
  outlet: 'reception' | 'bar_restaurant';
  amountCents: Cents;
  roomNumber?: string | null;
  resa?: string | null;
  /** The booking's property — the intent and the terminal's answer are filed
   *  under it; the terminal must serve it (§8.8n). Omitted = the terminal's own. */
  propertyCode?: PropertyCode | null;
}

// ── The fiscal chain (P3.1, PMS-P3-FISCAL-SCHEMA-BRIEF §2–§3) ─────────────────
// `pms_fiscal_document` and its lines — append-only, written only by
// `pms_fiscal_append` in the database. Nothing on this side computes a hash or
// a number: the transaction is the aggregate boundary (ADR-0012), and the
// domain only names what the database issued.

/** bigint identity — a register (`pms_caisse`), the unit of the chain. */
export type CaisseId = number;
/** bigint identity — one chained record (`pms_fiscal_document`). */
export type FiscalDocumentId = number;
/** bigint identity — one line of a chained record (`pms_fiscal_line`). */
export type FiscalLineId = number;

/** `invoice` (facture) or `credit_note` (avoir). P4 adds `ticket`. */
export type FiscalDocumentKind = 'invoice' | 'credit_note';

export interface FiscalLine {
  id: FiscalLineId;
  lineNo: number;
  kind: 'room' | 'product' | 'tax';
  labelFr: string;
  /** Negative on an avoir. */
  quantity: number;
  unitCents: Cents;
  amountCents: Cents;
  /** '10.00', '20.00'… — null is the exempt bucket (the taxe de séjour). */
  tvaRate: string | null;
  serviceStart: IsoDate;
  /** Exclusive. */
  serviceEnd: IsoDate;
  /** Set on an avoir line: the invoice line it negates. */
  creditsLineId: FiscalLineId | null;
}

export interface FiscalDocument {
  id: FiscalDocumentId;
  caisseId: CaisseId;
  kind: FiscalDocumentKind;
  /** Mode école — in the chain, excluded from every total (BOFiP §150). */
  isTraining: boolean;
  /** 'FA' · 'AV', 'E'-prefixed in mode école. */
  series: string;
  number: number;
  sequenceNumber: number;
  issuedAt: Instant;
  folioId: FolioId | null;
  creditsDocumentId: FiscalDocumentId | null;
  /** Signed: an avoir is negative. */
  totalTtcCents: Cents;
  vatBreakdown: { rate: string | null; ttcCents: Cents; htCents: Cents; vatCents: Cents }[];
  chainHash: string;
  lines: FiscalLine[];
}

// ── Payments and allocation (P3.2, PMS-P3-FISCAL-SCHEMA-BRIEF §4) ─────────────
// `pms_payment` — append-only; every balance is DERIVED in the database
// (`pms_payment_unapplied`, `pms_invoice_open`), never stored or computed here.

/** bigint identity — one movement (`pms_payment`). */
export type PaymentId = number;

/** receipt / transfer_in carry a balance; the rest consume one and name it. */
export type PaymentMovement = 'receipt' | 'transfer_in' | 'refund' | 'reversal' | 'forfeit' | 'transfer_out';

/** One per tender account in the FEC (51120000 … 51126000). `card` vs `amex`
 *  is decided by the terminal, never chosen at the desk. */
export type PaymentTender = 'card' | 'amex' | 'cash' | 'cheque' | 'ancv' | 'bank_transfer' | 'vad';

export interface Payment {
  id: PaymentId;
  folioId: FolioId;
  movement: PaymentMovement;
  tender: PaymentTender | null;
  /** Signed: negative for every consuming movement. */
  amountCents: Cents;
  isTraining: boolean;
  terminalTransactionId: TerminalTransactionId | null;
  receiptRef: string | null;
  sourcePaymentId: PaymentId | null;
  /** On a forfeit: the VAT returned on the deposit it keeps (Q4). */
  vatReturnedCents: Cents | null;
  reason: string | null;
  /** When the money moved — the device's clock for a card (G5). */
  authorisedAt: Instant;
  postedAt: Instant;
}

/** What a folio stands at — derived on read by `pms_folio_position`. */
export interface FolioPosition {
  /** Owed, not yet invoiced — what the brouillon shows. */
  unbilledCents: Cents;
  /** Invoiced, not yet settled. */
  invoicedOpenCents: Cents;
  /** Received, not yet applied — deposits, overpayments. */
  unappliedCents: Cents;
}

/** One payment offered at checkout: a card tap the terminal already produced,
 *  or a cash-type tender with its amount (and a reference where G4 wants one). */
export type CheckoutPayment =
  | { terminalTransactionId: TerminalTransactionId }
  | { tender: Exclude<PaymentTender, 'card' | 'amex'>; amountCents: Cents; receiptRef?: string };

// ── « Départs de demain » (PMS-TERMINAL-LINK-BRIEF §8.8) ─────────────────────
// The same sheet in both worlds: read from the Vega mirror until go-live, from
// the PMS folios after (the StayFeed pattern). Only the adapter changes.

/** One departing booking, as the breakfast desk needs it. */
export interface DepartureBill {
  /** The établissement it belongs to — the RPC returns it (U1). */
  propertyCode: PropertyCode;
  /** Vega's `resa` today; the PMS reservation after go-live. */
  bookingRef: string;
  /** The group's name for a group, the guest's otherwise. */
  partyLabel: string;
  /** As stored ('0214'); the UI shows them as on the door. */
  rooms: string[];
  /** The bill still to settle at checkout — today Vega's: the running bill's
   *  lines not yet invoiced (extras included) plus the FACT AUTO plan's nights
   *  not yet posted. What is already invoiced is not on it. Once the guest has
   *  LEFT (`checkedOut`): Vega's pending notes, already net of their arrhes. */
  billCents: Cents;
  folioLineCount: number;
  /** What the desk already collected against the booking. */
  collectedCents: Cents;
  /** « En attente » — shown beside the balance, never added into it. */
  pendingCents: Cents;
  /** What the guest already paid before checkout — Vega's arrhes today
   *  (GARRHES), netted off the balance. Signed: a refund reduces it. */
  depositCents: Cents;
  /** Arrhes held by a guest with SEVERAL bookings, so not attributable to this
   *  one — shown « à vérifier », never netted (Hard Rule 1). */
  depositUnattributedCents: Cents;
  /** A room the desk forgot to release in Vega (departure cut at the next arrival). */
  unreleased: boolean;
  /** « Vega relu à » — when the feed last CONFIRMED the mirror against Vega,
   *  not when a figure last changed (§8.8h). Null when unknown. */
  readAt: Instant | null;
  /** Every payment made on the booking, oldest first — how, when, how much. */
  payments: PaymentLine[];
  /** Vega says the guest has LEFT (status P). The bill is then the pending
   *  notes, and the arrhes are already inside them (§8.8h). */
  checkedOut: boolean;
  /** A guest who has left: Vega's notes still PENDING on the booking. */
  pendingNotes: PendingNote[];
  /** A guest who has left with no pending note: lines still LIVE on the bill
   *  and on no note — posted, never invoiced (§8.8i). A line the desk deleted
   *  is not one of them. Listed « non facturé · à vérifier », never counted. */
  unbilledLines: UnbilledLine[];
  /** The billing account this row is for, when the booking bills to more than
   *  one (§8.8j): 'GRP' for a group's own account, a room ('0110') for a
   *  room's extras. Null for a booking billed to a single account. */
  unit: string | null;
  /** 'groupe' for the group's account, the room's guest for a room's. */
  unitLabel: string | null;
  /** The booking's main account: it carries the plan, the arrhes and anything
   *  billed to no account. */
  isMain: boolean;
  /** When the last payment on this account was keyed — for « Heure de règlement ». */
  lastPaidAt: Instant | null;
  /** The account's « en attente » still open — the row settles or cancels each
   *  one (« Régler » / « Annuler », §8.8k: the Journal's job). */
  suspenses: OpenSuspense[];
  /** The booking's notes put on a debtor account at checkout (§8.8l — Vega's
   *  « Transfert débiteur »), on its main row. Not payments: the money is
   *  still owed, by the account named. */
  debts: TransferredNote[];
  /** Transferred less recovered since — what the debtor still owes. */
  debtCents: Cents;
  /** The booking's Vega client number (FORMATION 033276) — the sheets name a
   *  guest « NAME (033276) - 024608 » (operator 2026-09-26). */
  clientRef: string | null;
}

/** A note transferred to a debtor account (§8.8l). */
export interface TransferredNote {
  facture: string;
  date: IsoDate | null;
  at: Instant | null;
  cents: Cents;
  /** Recovered on this note since (Vega RECD). */
  recoveredCents: Cents;
  /** The debtor account's name — « CADAY ROUGE MOTO TOURS SA ». */
  debtor: string | null;
  /** The note's billing account: 'GRP' or a room. */
  account: string | null;
}

/** A movement on a debtor account (§8.8l): a note transferred to it (Vega's
 *  « Transfert débiteur », GDETREG CLDI 998), or a recovery (RECD). */
export interface DebtorMovement {
  kind: 'transfer' | 'recovery';
  facture: string | null;
  date: IsoDate | null;
  at: Instant | null;
  /** The recovery's tender — « Virement », « Impayé », « Écart »; null on a transfer. */
  label: string | null;
  /** Signed as Vega records it: a credit note transferred is negative. */
  cents: Cents;
  bookingRef: string | null;
  partyLabel: string | null;
  /** The note's billing account: 'GRP' or a room. */
  account: string | null;
}

/** « Clients débiteurs » (§8.8l): a debtor account with a balance — the notes
 *  transferred to it less what was recovered, since it last stood at zero. */
export interface DebtorAccount {
  /** The établissement it belongs to — the RPC returns it (U1). */
  propertyCode: PropertyCode;
  /** Vega's client number for the account. */
  clientRef: string;
  name: string;
  balanceCents: Cents;
  /** The day the oldest open transfer was made. */
  openedOn: IsoDate | null;
  /** Since the account last stood at zero, oldest first. */
  movements: DebtorMovement[];
  readAt: Instant | null;
}

/** An « en attente » taken in the Encaissement form and not yet settled or
 *  cancelled — what the guest should still pay, known at capture. */
export interface OpenSuspense {
  id: ReceptionPaymentId;
  cents: Cents;
  at: Instant;
  /** What was typed in « Chambre ou résa » when it was taken. */
  roomNumber: string | null;
}

/** One payment taken in the Encaissement form on a day — what the retired
 *  Journal listed (§8.8k), with whether Vega shows it yet. */
export interface DayPayment {
  /** The établissement it belongs to — the RPC returns it (U1). */
  propertyCode: PropertyCode;
  id: ReceptionPaymentId;
  bookingRef: string | null;
  roomNumber: string | null;
  partyLabel: string | null;
  tenderType: TenderType;
  /** For an « en attente »: what the guest should pay. Zero for an annulation. */
  cents: Cents;
  status: ReceptionPaymentStatus;
  capturedAt: Instant;
  /** Money the mirror now shows in Vega: same booking, same amount, same kind
   *  of tender, one Vega line per payment. Derived on read, never stored. */
  inVega: boolean;
  /** An « en attente » already settled or cancelled. */
  closed: boolean;
  /** Its booking arrives / leaves that day — so it is on that day's sheet. */
  arrives: boolean;
  departs: boolean;
}

/** Where a day's money is counted in « Encaissements du jour » (§8.8o): one
 *  tender each, card split by the statement it reconciles against (CB, AMEX,
 *  VAD — operator 2026-09-26), or `outside` — a Vega movement that is neither
 *  money in nor a refund of a known tender (a deposit refund with no mode, a
 *  settlement gap, a « prise en charge »), shown so nothing is hidden, never
 *  counted in the total. */
export type TakingsBucket = 'cash' | 'cheque' | 'ancv' | 'card' | 'amex' | 'vad' | 'transfer' | 'outside';

/** One bucket of the day's takings, for one property: Vega's movements dated
 *  that day plus the form payments Vega does not show yet. Refunds count
 *  where the money came in, negative (operator 2026-09-26). */
export interface DayTakingsLine {
  propertyCode: string;
  bucket: TakingsBucket;
  /** What an `outside` movement is (« Rembt arrhes », « Écart »…); null otherwise. */
  label: string | null;
  /** Money in, ≥ 0. */
  inCents: Cents;
  /** Money out — refunds — ≤ 0. */
  outCents: Cents;
  movements: number;
  /** Of it, taken in the form and not yet keyed into Vega. */
  notInVegaCents: Cents;
  notInVega: number;
}

/** The desk's two shifts (§8.8p, operator 2026-09-27): the check-out shift
 *  works the Départs sheet, the check-in shift the Arrivées sheet. */
export type DeskShift = 'checkin' | 'checkout';

/** A payment of the day for a guest on NEITHER sheet — a debtor paying, a
 *  mid-stay guest, a note paid the morning after leaving, a bar sale, a
 *  deposit for a later stay (§8.8p) — in the shift it belongs to: the tab its
 *  form entry was taken on, else check-in. */
export interface OtherTaking {
  propertyCode: string;
  shift: DeskShift;
  /** Vega's line, or the form's while Vega does not show it (⚠). */
  source: 'vega' | 'form';
  /** Vega's movement code (ARVE, REGL, RECD…); null for the form's. */
  kind: string | null;
  method: string | null;
  bucket: TakingsBucket;
  /** What an `outside` movement is; null otherwise. */
  label: string | null;
  cents: Cents;
  at: Instant | null;
  bookingRef: string | null;
  /** The note it settles, for a règlement. */
  facture: string | null;
  partyLabel: string | null;
  arrivalDate: IsoDate | null;
  departureDate: IsoDate | null;
}

/** One movement on a no-show's booking. */
export interface NoShowPayment {
  date: IsoDate | null;
  at: Instant | null;
  kind: string;
  method: string | null;
  label: string | null;
  cents: Cents;
}

/** A booking due to arrive that Vega cancelled on or after its arrival day
 *  (§8.8p) — a cancelled booking leaves Vega's live book, so this is where a
 *  past day's no-shows are read from. */
export interface NoShow {
  propertyCode: string;
  bookingRef: string;
  /** The name Vega keyed on a payment for the booking, when there is one —
   *  the guest master is not read here (operator-only). */
  name: string | null;
  cancelledOn: IsoDate | null;
  /** 'HHMM'. */
  cancelledAt: string | null;
  /** Vega's « Motif », free text (« NO SHOW », « (R) CXX_NO_FEE »…). */
  reason: string | null;
  rooms: number | null;
  /** Vega's « CA perdu »: the stay's value. */
  lostCents: Cents | null;
  /** The desk's clerical cancellations (Err, Double resa, Block, Dans le
   *  groupe) — counted, not listed (operator 2026-09-27). */
  clerical: boolean;
  payments: NoShowPayment[];
}

/** A Vega note still « en attente » on a booking (GFACTURE status A). */
export interface PendingNote {
  facture: string;
  date: IsoDate | null;
  /** Net of the arrhes the note absorbed — what Vega says is still owed. */
  cents: Cents;
}

/** A line left on a departed guest's bill, on no note. */
export interface UnbilledLine {
  label: string | null;
  date: IsoDate | null;
  qty: number | null;
  unitPriceCents: Cents | null;
  cents: Cents;
}

/** « Non facturés » (§8.8i): a stay over whose bill was posted and never
 *  invoiced — live lines not yet invoiced, no pending note, guests gone
 *  (TRAVEL COUNSELLORS, 13/09/2026: 3 732,96 posted, the same held in arrhes). */
export interface UnissuedBill {
  propertyCode: string;
  bookingRef: string;
  partyLabel: string;
  rooms: string[];
  arrivalDate: IsoDate | null;
  departureDate: IsoDate | null;
  /** Vega's status: 'departed', or 'in_house' when the departure passed with no checkout. */
  vegaStatus: string | null;
  lineCount: number;
  firstDay: IsoDate | null;
  lastDay: IsoDate | null;
  /** What the lines not yet invoiced come to — the invoice to make. Signed. */
  amountCents: Cents;
  /** Arrhes Vega still holds for the booking — usually what settles it. */
  depositCents: Cents;
  depositUnattributedCents: Cents;
  payments: PaymentLine[];
  readAt: Instant | null;
  /** The booking's Vega client number (FORMATION 033276) — the sheets name a
   *  guest « NAME (033276) - 024608 » (operator 2026-09-26). */
  clientRef: string | null;
}

/** A booking the operator set aside from « Non facturés » — a leftover on a
 *  closed period that Vega's own screens can no longer reach (LASSALLE, an 11,00
 *  breakfast posted after a settled invoice, February 2026). Reversible. */
export interface DismissedUnissued {
  propertyCode: string;
  bookingRef: string;
  partyLabel: string;
  lineCount: number;
  amountCents: Cents;
  firstDay: IsoDate | null;
  lastDay: IsoDate | null;
  reason: string;
  dismissedAt: Instant;
}

/** One payment on a booking, as the Départs / Arrivées sheets list it
 *  (operator 2026-09-25: HOW a guest paid — card or not — is part of the
 *  checks, so payments are listed, never only totalled). */
export interface PaymentLine {
  date: IsoDate | null;
  /** When it was keyed, to the second — Vega's record header (§8.8j), or the
   *  Encaissement log's capture. Null when unknown. */
  at: Instant | null;
  /** Where it was read: Vega's arrhes (taken before invoicing), a Vega
   *  règlement on the booking's invoice, or the desk's Encaissement log while
   *  Vega does not show it yet — once it does, Vega's own line stands for it
   *  (§8.8k), so an 'encaissement' line is always one still to key. */
  source: 'arrhes' | 'reglement' | 'encaissement';
  /** Vega's method code (`010`…) or the log's tender type. */
  method: string | null;
  /** Short French label — « CB », « Espèces », « Virement »… */
  label: string | null;
  /** Card or not. Null for a refund or an adjustment, which is neither. */
  isCard: boolean | null;
  /** Signed — a refund is negative. */
  cents: Cents;
}

/** What ONE booking has already paid — « Arrhes » on the planning pane and
 *  the booking form. The same money the « Arrivées » sheet reads for a booking,
 *  asked for by reservation instead of by arrival day. */
export interface BookingAdvance {
  /** Arrhes Vega still holds for the booking. */
  depositCents: Cents;
  /** Arrhes Vega holds that cannot be placed on one booking — shown apart,
   *  never added in (Hard Rule 1). */
  depositUnattributedCents: Cents;
  /** Taken in the Encaissement form and not yet shown by Vega (§8.8k). */
  collectedCents: Cents;
  /** Every payment on the booking, oldest first. */
  payments: PaymentLine[];
  readAt: Instant | null;
}

/** What one booking has paid, in total — « Réservations »' « Paiement » column,
 *  which asks for hundreds of bookings at once. The same payment list as
 *  `BookingAdvance.payments`, summed; the list holds each booking's total, so the
 *  status is worked out there. */
export interface BookingPaid {
  reservationId: ReservationId;
  /** Everything paid towards the booking (signed: a refund reduces it). */
  paidCents: Cents;
  /** Of which, what Vega recorded as arrhes. */
  arrhesCents: Cents;
}

/** A note Vega still holds open on a booking, as « Suivi des paiements » reads
 *  it: what is still owed on it, and the arrhes it already absorbed. */
export interface BookingOpenNote extends PendingNote {
  /** The arrhes the note absorbed (Vega's « arrhes déduites »). A note whose
   *  arrhes exceed the bill reads negative: money owed back to the guest. */
  appliedCents: Cents;
  /** The note's billing account: 'GRP' or a room ('0110'); null when unknown. */
  account: string | null;
  /** Who slept in the room the note bills; null for the group's own account
   *  or when Vega does not say. */
  guest: string | null;
}

/** « Suivi des paiements » (Encaissement, 20261024100100): one booking the PMS
 *  holds and its money — what it comes to, what was paid, and, once the stay is
 *  over, what Vega says is still owed. The figures only: the status is read
 *  from them by the screen, one rule per phase (past: Vega's view; to come or in
 *  house: the total against what was paid). */
export interface BookingBalance {
  reservationId: ReservationId;
  propertyCode: PropertyCode;
  /** Vega's number for the booking; null for one made here. */
  bookingRef: string | null;
  /** The booking's PMS status (`confirmed`, `checked_in`, `checked_out`…). */
  status: string;
  partyLabel: string;
  /** The booking's Vega client number — the sheets' « NAME (033276) - 024608 ». */
  clientRef: string | null;
  /** The channel's reference (Booking.com, Expedia…), for the search. */
  externalRef: string | null;
  rooms: string[];
  arrivalDate: IsoDate;
  departureDate: IsoDate;
  /** What the booking comes to — « Réservations »' total. */
  totalCents: Cents;
  /** Everything paid towards it (signed): Vega's payments and the form's not
   *  yet in Vega — the same list `BookingAdvance.payments` holds, summed. */
  paidCents: Cents;
  /** Of which, what Vega recorded as arrhes. */
  arrhesCents: Cents;
  /** The day of the last payment, when there is one. */
  lastPaidOn: IsoDate | null;
  /** Of `paidCents`, taken in the form and not yet shown by Vega (§8.8k). */
  collectedCents: Cents;
  /** « En attente » still open on the booking — never counted as paid. */
  pendingCents: Cents;
  /** Arrhes Vega still holds for the booking — on a stay over, arrhes never
   *  applied to its bill unless an open note absorbed them. */
  depositCents: Cents;
  /** Arrhes Vega holds for the guest that cannot be placed on one booking. */
  depositUnattributedCents: Cents;
  /** Notes still open — on a stay over, what Vega says is owed (or owed back).
   *  On a booking Vega also invoiced, a side note (a guest's extras left
   *  behind); on one it never invoiced, the stay itself. */
  openNotes: BookingOpenNote[];
  /** Notes put on a debtor account and not yet recovered (§8.8l). */
  debtCents: Cents;
  debts: TransferredNote[];
  /** Lines posted and never invoiced, guests gone (« Non facturés », §8.8i). */
  unbilledCents: Cents;
  unbilledLines: number;
  readAt: Instant | null;
  /** The booking's invoices in Vega's journal (signed). A note « en attente »
   *  is never in it, so 0 with an open note means the stay was left on it. */
  invoicedCents: Cents;
}

/** What a `VegaPlanGap` is about, night by night against the room allocation:
 *  · `rooms`     — a board billed for another number of rooms than the allocation holds in it;
 *  · `persons`   — a board priced per person billed for another number of people;
 *  · `board`     — billed in a board the allocation puts no logement in;
 *  · `unbilled`  — the allocation's logements of a board, billed in none of it;
 *  · `occupancy` — a product for k persons on more rooms than hold k persons. */
export type VegaPlanGapKind = 'rooms' | 'persons' | 'board' | 'unbilled' | 'occupancy';

/** Where Vega's TWO records of one group disagree — the charges (the plan) and the
 *  room allocation (rooms, their board and the number of people in each), which Vega
 *  keeps apart and never checks against each other (operator, 2026-10-22/23). Read
 *  by `pms_reservation_vega_plan_gap` night by night; the FIRST disagreement. The
 *  total stays Vega's: this is a flag, never a correction (RIVENDELL 022732 is
 *  billed as the operator wants, and still flagged). */
export interface VegaPlanGap {
  /** The first night the two disagree. */
  firstNight: IsoDate;
  /** Nights that disagree, out of the plan's nights. */
  gapNights: number;
  planNights: number;
  kind: VegaPlanGapKind;
  /** The board the disagreement is in — the billed line's (from its product's
   *  label), or the allocation's for `unbilled`; null for lines naming none. */
  board: PmsBoard | null;
  /** `board` only: the board most of the allocation's logements are in that night. */
  allocationBoard: PmsBoard | null;
  /** Rooms — persons for `persons` — Vega's charges bill that night in `board`. */
  billed: number;
  /** The allocation's logements in `board` that night (all boards when null); for
   *  `occupancy`, the logements that hold `occupancy` persons. */
  rooms: number;
  /** The persons in them; null for `occupancy`. */
  persons: number | null;
  /** `occupancy` only: the persons the billed product is for. */
  occupancy: number | null;
  /** The seed could split the charges over the rooms: false = the per-room prices
   *  shown are the PMS's own spread. */
  split: boolean;
  /** The seed's own sentence when it could not split (`planNonReparti`). */
  detail: string | null;
}

/** One booking arriving on a date — the « Arrivées » sheet. */
export interface ArrivalBill {
  /** The établissement it belongs to — the RPC returns it (U1). */
  propertyCode: PropertyCode;
  bookingRef: string;
  partyLabel: string;
  rooms: string[];
  arrivalDate: IsoDate;
  departureDate: IsoDate;
  /** Checked in — Vega's status A (in house) or P (departed). */
  arrived: boolean;
  /** 'HHMM' — the real check-in time once arrived, the expected one before. */
  arrivalTime: string | null;
  /** The whole stay from Vega's FACT AUTO plan — its « Total séjour ». */
  stayCents: Cents;
  /** Arrhes Vega still holds for the booking. */
  depositCents: Cents;
  /** Arrhes Vega holds that cannot be placed on one booking — shown apart. */
  depositUnattributedCents: Cents;
  payments: PaymentLine[];
  /** « Vega relu à » — the same instant as the departures sheet's. */
  readAt: Instant | null;
  /** Taken in the Encaissement form and not yet shown by Vega (§8.8k). */
  collectedCents: Cents;
  /** The booking's Vega client number (FORMATION 033276) — the sheets name a
   *  guest « NAME (033276) - 024608 » (operator 2026-09-26). */
  clientRef: string | null;
  /** « En attente » still open on the booking — never subtracted, like Départs. */
  pendingCents: Cents;
  /** Each open « en attente », for « Régler » / « Annuler » on the row. */
  suspenses: OpenSuspense[];
}

/** One booking still to arrive — the team's « Arrivées » list (staff.*).
 *  Deliberately thin: no name, no payment lines. */
export interface FrontDeskArrival {
  rooms: string[];
  nights: number;
  /** What is left to collect; null when Vega's plan prices no stay. May be
   *  zero or negative (settled / overpaid). */
  dueCents: Cents | null;
  /** 'HHMM' — the expected arrival time, when known. */
  expectedTime: string | null;
  readAt: Instant | null;
}

/** One departure with something left to collect — the team's « Départs » list. */
export interface FrontDeskDeparture {
  rooms: string[];
  nights: number;
  dueCents: Cents;
  readAt: Instant | null;
}
